/**
 * PAYMENT RETURN — where Stripe sends the member back
 *
 * It claims nothing. It asks our own server, which asks Stripe, until Stripe has answered —
 * and only then says the subscription is active. If the answer has not arrived yet it says so
 * plainly and lets the member carry on; the webhook and reconciliation both finish the job
 * server-side either way.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Check, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentReturn = ({ lang = 'en', outcome = 'success' }) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState(outcome === 'success' ? 'checking' : 'cancelled');

  useEffect(() => {
    if (outcome !== 'success') return undefined;
    const sessionId = params.get('session_id');
    if (!sessionId) {
      setState('pending');
      return undefined;
    }
    const isAddon = params.get('addon') === '1';  // Stage 2: one-time add-on purchase
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(isAddon ? `${API}/addons/checkout/status/${sessionId}`
                                                 : `${API}/subscription/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (isAddon ? data?.credited : data?.activated) {
          setState(isAddon ? 'addon' : 'active');
          setTimeout(() => navigate('/home'), 2600);
          return;
        }
      } catch (e) {
        // Fall through and try again; nothing is claimed on a failed read.
      }
      if (cancelled) return;
      if (attempts >= 8) {
        setState('pending');
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [outcome, params, navigate]);

  const copy = {
    checking: lang === 'es' ? 'Confirmando tu suscripción…' : 'Confirming your subscription…',
    active: lang === 'es' ? 'Tu membresía está activa.' : 'Your membership is active.',
    addon: lang === 'es' ? 'Tu complemento está listo.' : 'Your add-on is ready.',
    pending: lang === 'es'
      ? 'Tu pago se está procesando. Se activará en cuanto tu banco lo confirme.'
      : "Your payment is processing. It activates as soon as your bank confirms it.",
    cancelled: lang === 'es'
      ? 'No se ha cobrado nada. Puedes volver cuando quieras.'
      : 'Nothing was charged. You can come back whenever you like.',
  }[state];

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6 text-center"
      style={{ background: 'linear-gradient(180deg, #070A12 0%, #04060B 100%)' }}
      data-testid="payment-return"
    >
      {state === 'checking' && (
        <Loader2 size={22} className="animate-spin mb-6" style={{ color: '#E8C58F' }} />
      )}
      {(state === 'active' || state === 'addon') && (
        <Check size={26} className="mb-6" style={{ color: '#E8C58F' }} />
      )}
      <p
        className="text-[#F4EFE7]/80 text-base max-w-sm"
        style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 22 }}
        data-testid="payment-return-message"
      >
        {copy}
      </p>
      <button
        onClick={() => navigate('/home')}
        className="mt-10 px-6 py-3 rounded-xl text-sm transition-colors"
        style={{ background: 'rgba(232,197,143,0.08)', border: '1px solid rgba(232,197,143,0.4)',
                 color: '#F4EFE7' }}
        data-testid="payment-return-continue"
      >
        {lang === 'es' ? 'Continuar' : 'Continue'}
      </button>
    </div>
  );
};

export default PaymentReturn;
